import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/Spinner';
import { formatMoney } from '@/lib/format';
import { useCampaignPerformance } from '@/features/campaigns/useCampaigns';
import { useCommissions } from '@/features/deals/useDeals';
import { FunnelChart } from './Dashboards';
import { StatTile } from './StatTile';
import { useMonthlyKpis } from './useReports';

const num = (v: string | number | null | undefined) => Number(v ?? 0);
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

/**
 * Marketing and finance get their own dashboards rather than the manager one.
 *
 * Not a cosmetic choice. The manager dashboard leads with revenue per agent, and
 * neither role can read `deals` — marketing has no deal access at all, finance has
 * no lead access — so they would be shown a table of zeros that looks like the
 * company sold nothing. A dashboard that silently reports zero because of RLS is
 * worse than one that does not show the number: the first is wrong, the second is
 * merely incomplete.
 */

// -------------------------------------------------------------- marketing

export function MarketingDashboard() {
  const { t } = useTranslation();
  const campaigns = useCampaignPerformance();

  if (campaigns.isPending) return <Spinner />;

  const rows = campaigns.data ?? [];
  const spend = sum(rows.map((r) => num(r.spend)));
  const leads = sum(rows.map((r) => r.leads));
  const qualified = sum(rows.map((r) => r.qualified));
  const commission = sum(rows.map((r) => num(r.commission)));

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('dashboard.marketingSpend')} value={formatMoney(spend)} />
        <StatTile label={t('dashboard.leads')} value={leads} />
        <StatTile
          label={t('dashboard.cpl')}
          value={leads === 0 ? '—' : formatMoney(spend / leads)}
          hint={t('dashboard.cplHint')}
        />
        <StatTile
          label={t('dashboard.roas')}
          value={spend === 0 ? '—' : `${(commission / spend).toFixed(2)}×`}
          tone={commission >= spend ? 'good' : 'warn'}
        />
      </div>

      {/* Cost per qualified lead is the number that actually distinguishes
          campaigns (#18); volume flatters the cheap ones. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('campaigns.columns.qualified')} value={qualified} />
        <StatTile
          label={t('campaigns.columns.cpql')}
          value={qualified === 0 ? '—' : formatMoney(spend / qualified)}
        />
        <StatTile
          label={t('dashboard.sales')}
          value={sum(rows.map((r) => r.sales))}
          hint={t('dashboard.fromCampaigns')}
        />
        <StatTile
          label={t('dashboard.neverContacted')}
          value={sum(rows.map((r) => r.never_contacted))}
          tone={sum(rows.map((r) => r.never_contacted)) > 0 ? 'bad' : 'good'}
          hint={t('dashboard.paidForNotCalled')}
        />
      </div>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">{t('dashboard.campaigns')}</h2>
          <Link to="/campaigns" className="text-xs font-medium text-brand-700 hover:underline">
            {t('dashboard.openCampaigns')}
          </Link>
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-start">{t('campaigns.columns.name')}</th>
                <th className="px-3 py-2 text-start">{t('campaigns.columns.spend')}</th>
                <th className="px-3 py-2 text-start">{t('campaigns.columns.leads')}</th>
                <th className="px-3 py-2 text-start">{t('campaigns.columns.cpql')}</th>
                <th className="px-3 py-2 text-start">{t('campaigns.columns.roas')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.campaign_id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-900">{row.campaign_name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                    {formatMoney(row.spend)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">{row.leads}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                    {formatMoney(row.cpql)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-700" dir="ltr">
                    {row.roas ? `${row.roas}×` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.funnel')}</h2>
        <FunnelChart />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- finance

export function FinanceDashboard() {
  const { t } = useTranslation();
  const commissions = useCommissions({});
  const kpis = useMonthlyKpis();

  if (commissions.isPending) return <Spinner />;

  const rows = commissions.data ?? [];
  const by = (status: string) =>
    rows.filter((r) => r.status === status).reduce((a, r) => a + num(r.gross_commission), 0);

  const months = kpis.data ?? [];
  const thisMonth = months[months.length - 1];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('commissionStatuses.expected')} value={formatMoney(by('expected'))} />
        <StatTile
          label={t('commissionStatuses.due')}
          value={formatMoney(by('due'))}
          tone={by('due') > 0 ? 'warn' : 'neutral'}
        />
        <StatTile label={t('commissionStatuses.invoiced')} value={formatMoney(by('invoiced'))} />
        <StatTile
          label={t('commissionStatuses.received')}
          value={formatMoney(by('received'))}
          tone="good"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('dashboard.revenue')} value={formatMoney(thisMonth?.revenue)} />
        <StatTile label={t('dashboard.commission')} value={formatMoney(thisMonth?.commission)} />
        <StatTile label={t('dashboard.sales')} value={thisMonth?.sales ?? 0} />
        <StatTile
          label={t('dashboard.cancellations')}
          value={thisMonth?.cancellations ?? 0}
          tone={(thisMonth?.cancellations ?? 0) > 0 ? 'warn' : 'good'}
        />
      </div>

      <Link
        to="/commissions"
        className="rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-brand-800 hover:bg-slate-50"
      >
        {t('dashboard.openCommissions')}
      </Link>
    </div>
  );
}
